import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { CommunityResource } from '@/types/residents/communityResource';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { ResourceIcon } from '@/components/residents/resources/ResourceIcon';
import { GlassCard } from '../GlassCard';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface ResourceGlassTileProps {
  item: CommunityResource;
  onPress: (item: CommunityResource) => void;
}

const ResourceGlassTileBase: React.FC<ResourceGlassTileProps> = ({ item, onPress }) => {
  const { colors } = useThemeColors();
  const [hovered, setHovered] = useState(false);

  const totalItems = useMemo(() => (
    [
      ...(item.files || []),
      ...(item.url ? [item.url] : []),
      ...(item.content ? [item.content] : []),
    ].length
  ), [item]);

  return (
    <View style={styles.wrapper}>
      <Pressable
        onPress={() => onPress(item)}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        accessibilityRole="button"
      >
        <GlassCard
          borderRadius={REDESIGN_SPACING.quickActionRadius}
          padding={20}
          style={[styles.card, hovered && styles.cardHovered]}
        >
          <View style={[styles.iconCircle, { backgroundColor: colors.accent.blueBgLight }]}>
            <ResourceIcon
              iconKey={item.icon || item.iconName}
              size={20}
              color={colors.text.primary}
            />
          </View>

          <View style={styles.titleRow}>
            <Text style={[styles.title, { color: colors.text.primary }]} numberOfLines={1}>{item.name || item.title}</Text>
            {totalItems > 0 && (
              <View style={styles.countPill}>
                <Text style={[styles.countPillText, { color: colors.text.muted }]}>{totalItems}</Text>
              </View>
            )}
          </View>

          {item.description ? (
            <Text style={[styles.description, { color: colors.text.dim }]} numberOfLines={2}>
              {item.description}
            </Text>
          ) : null}
        </GlassCard>
      </Pressable>
    </View>
  );
};

ResourceGlassTileBase.displayName = 'ResourceGlassTile';

export const ResourceGlassTile = React.memo(ResourceGlassTileBase);

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  card: {
    minHeight: 160,
    gap: 10,
  },
  cardHovered: {
    borderColor: 'rgba(255,255,255,0.12)',
  },
  iconCircle: {
    width: REDESIGN_SPACING.iconSize.resourceIcon,
    height: REDESIGN_SPACING.iconSize.resourceIcon,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 2,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    flexShrink: 1,
  },
  countPill: {
    backgroundColor: 'rgba(255,255,255,0.08)',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 9999,
  },
  countPillText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  description: {
    ...REDESIGN_TYPOGRAPHY.body,
    marginTop: 2,
  },
});
